import { AnimatePresence, motion } from "framer-motion"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { CSSProperties } from "react"
import { Link } from "react-router-dom"
import { useTheme } from "../../theme/ThemeProvider"

type Persona = "demon" | "fairy"

type PersonaText = string | { demon: string; fairy: string }

type DemoMessage = {
  from: "you" | "agent"
  text: PersonaText
}

type SeenCheck = {
  label: string
  state: "clear" | "confirm" | "info"
}

type DemoStep = {
  chapter: number
  msg?: DemoMessage
  fields?: Record<string, string>
  checks?: SeenCheck[]
}

type Chapter = {
  title: string
  why: string
}

const CHAPTERS: Chapter[] = [
  { title: "Invoice", why: "Invoice from your phone in one text — no app, no forms." },
  { title: "Confirm", why: "Money never auto-sends on a guess — anything risky gets confirmed first." },
  { title: "Paid", why: "Stripe pay link goes out; the money lands in your account." },
  { title: "Chase", why: "Overdue? Your agent chases politely so you don't have to." },
  { title: "Repeat", why: "Every customer, invoice, and payment lands in your dashboard automatically." },
]

const FIELD_ORDER = [
  "Customer",
  "Email",
  "Amount",
  "Deposit",
  "Description",
  "Invoice",
  "Balance",
  "Next job",
] as const

const STEPS: DemoStep[] = [
  {
    chapter: 0,
    msg: {
      from: "agent",
      text: {
        demon: "🔥 Ready when you are. Text me a job and I'll handle the money.",
        fairy: "✨ Ready when you are. Text me a job and I'll handle the money.",
      },
    },
  },
  {
    chapter: 0,
    msg: { from: "you", text: "invoice sam 1450 for the website project, 25% deposit. email is sam@fastmail.com" },
    fields: {
      Customer: "Sam",
      Email: "sam@fastmail.com",
      Amount: "$1,450.00",
      Deposit: "25% · $362.50 now",
      Description: "website project",
    },
    checks: [
      { label: "Amount parsed", state: "clear" },
      { label: "Email parsed", state: "clear" },
      { label: "Over $500 — confirm required", state: "confirm" },
    ],
  },
  {
    chapter: 1,
    msg: {
      from: "agent",
      text:
        "Heads up — Amount tops the 500.00 limit. I read $1,450.00 to sam@fastmail.com — \"website project\", 25% deposit ($362.50 now). Reply YES to send, or tweak: AMOUNT 1450 | EMAIL sam@fastmail.com | DESC \"website project\"",
    },
  },
  {
    chapter: 1,
    msg: { from: "you", text: "YES" },
  },
  {
    chapter: 1,
    msg: {
      from: "agent",
      text: {
        demon: "Invoice INV-114 sent to sam@fastmail.com. Pay link is live. — your Demon 🔥",
        fairy: "Invoice INV-114 sent to sam@fastmail.com. Pay link is live. — your Fairy ✨",
      },
    },
    fields: { Invoice: "INV-114 · sent" },
    checks: [
      { label: "Confirmed by you", state: "clear" },
      { label: "Pay link live", state: "clear" },
    ],
  },
  {
    chapter: 2,
    msg: { from: "agent", text: "💸 Sam paid the $362.50 deposit. Balance $1,087.50 due on completion." },
    fields: { Invoice: "INV-114 · deposit paid", Balance: "$1,087.50 due" },
    checks: [{ label: "Deposit settled via Stripe", state: "clear" }],
  },
  {
    chapter: 3,
    msg: { from: "you", text: "project's done, send the balance" },
  },
  {
    chapter: 3,
    msg: { from: "agent", text: "Balance invoice for $1,087.50 sent to Sam. I'll watch it and chase if it slips." },
    fields: { Invoice: "INV-114 · balance sent" },
  },
  {
    chapter: 3,
    msg: {
      from: "agent",
      text:
        "Day 7 — no payment yet. I nudged Sam: \"Friendly reminder: $1,087.50 for the website project is due. Pay here: …\" Customers always get plain, professional messages.",
    },
    checks: [
      { label: "Reminder sent — plain voice to customers", state: "info" },
    ],
  },
  {
    chapter: 3,
    msg: { from: "agent", text: "💸 Sam paid $1,087.50. Paid in full." },
    fields: { Invoice: "INV-114 · paid in full", Balance: "$0.00" },
    checks: [{ label: "Paid in full", state: "clear" }],
  },
  {
    chapter: 4,
    msg: { from: "you", text: "legend. sam wants another project next month, remind me to quote it" },
  },
  {
    chapter: 4,
    msg: {
      from: "agent",
      text: {
        demon: "Noted — I'll remind you on the 12th. Sam's record: 2 invoices, pays in ~3 days. A keeper. — your Demon 🔥",
        fairy: "Noted — I'll remind you on the 12th. Sam's record: 2 invoices, pays in ~3 days. A keeper. — your Fairy ✨",
      },
    },
    fields: { "Next job": "New quote · reminder set" },
    checks: [
      { label: "Reminder scheduled", state: "clear" },
      { label: "Customer history on file", state: "info" },
    ],
  },
]

function personaText(text: PersonaText, persona: Persona): string {
  return typeof text === "string" ? text : text[persona]
}

const MONO = '"IBM Plex Mono","Space Mono",ui-monospace,Menlo,Consolas,monospace'

export type ChapteredDemoProps = {
  /** Embedded mode drops the brand header and page-height framing so the
      demo can live inside another page (e.g. how-it-works on mobile). */
  embedded?: boolean
}

export default function ChapteredDemo({ embedded = false }: ChapteredDemoProps) {
  // Persona is the global theme: demon = dark, fairy = light. Toggling here
  // flips the whole page, not just the demo.
  const { theme, toggleTheme } = useTheme()
  const persona: Persona = theme === "fairy" ? "fairy" : "demon"
  const [stepIndex, setStepIndex] = useState(0)
  const [autoPlay, setAutoPlay] = useState(false)
  const [mobilePane, setMobilePane] = useState<"phone" | "seen">("phone")
  const threadRef = useRef<HTMLDivElement | null>(null)

  const isDemon = persona === "demon"
  const accent = isDemon ? "#ff2b4f" : "#2ed6b5"
  const accentText = "var(--accent-text)"
  const agentName = isDemon ? "Your Demon" : "Your Fairy"
  const agentTagline = "AI invoicing agent · on call 24/7"
  // Surface colors that hold up on both the dark demon and light fairy themes.
  const surface = isDemon
    ? {
        panel: "rgba(18,18,20,0.65)",
        hairline: "rgba(255,255,255,0.07)",
        youBorder: "rgba(255,255,255,0.22)",
        youBg: "rgba(255,255,255,0.06)",
        faint: "var(--text-faint)",
        railIdle: "rgba(255,255,255,0.14)",
        glow: "rgba(255,43,79,0.14)",
        agentBg: "rgba(255,43,79,0.08)",
      }
    : {
        panel: "rgba(255,255,255,0.9)",
        hairline: "rgba(24,32,51,0.1)",
        youBorder: "rgba(24,32,51,0.3)",
        youBg: "rgba(24,32,51,0.05)",
        faint: "var(--text-faint)",
        railIdle: "rgba(24,32,51,0.14)",
        glow: "rgba(46,214,181,0.2)",
        agentBg: "rgba(46,214,181,0.1)",
      }

  const atEnd = stepIndex >= STEPS.length - 1
  const currentChapter = STEPS[stepIndex]?.chapter ?? 0

  const visibleSteps = useMemo(() => STEPS.slice(0, stepIndex + 1), [stepIndex])
  const messages = useMemo(
    () => visibleSteps.filter((step) => step.msg).map((step) => step.msg as DemoMessage),
    [visibleSteps],
  )
  const fields = useMemo(() => {
    const merged: Record<string, string> = {}
    for (const step of visibleSteps) {
      Object.assign(merged, step.fields ?? {})
    }
    return merged
  }, [visibleSteps])
  const checks = useMemo(() => {
    for (let i = visibleSteps.length - 1; i >= 0; i -= 1) {
      if (visibleSteps[i].checks) return visibleSteps[i].checks as SeenCheck[]
    }
    return [] as SeenCheck[]
  }, [visibleSteps])

  const advance = useCallback(() => {
    setStepIndex((prev) => Math.min(prev + 1, STEPS.length - 1))
  }, [])

  const restart = useCallback(() => {
    setStepIndex(0)
    setAutoPlay(false)
  }, [])

  const jumpToChapter = useCallback((chapter: number) => {
    const first = STEPS.findIndex((step) => step.chapter === chapter)
    if (first >= 0) {
      // land on the last step of the chosen chapter's opening beat
      setStepIndex(first)
    }
  }, [])

  useEffect(() => {
    if (!autoPlay || atEnd) return
    const timer = window.setInterval(() => {
      setStepIndex((prev) => {
        if (prev >= STEPS.length - 1) {
          window.clearInterval(timer)
          return prev
        }
        return prev + 1
      })
    }, 2300)
    return () => window.clearInterval(timer)
  }, [autoPlay, atEnd])

  useEffect(() => {
    const node = threadRef.current
    if (node) node.scrollTop = node.scrollHeight
  }, [stepIndex])

  const pill = (active: boolean): CSSProperties => ({
    padding: "8px 12px",
    border: active ? `2px solid ${accent}` : "1px solid var(--rail)",
    background: active ? (isDemon ? "rgba(255,255,255,0.04)" : "rgba(24,32,51,0.04)") : "transparent",
    color: active ? "var(--text)" : "var(--muted)",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    fontSize: "11px",
    cursor: "pointer",
    minHeight: "40px",
    fontFamily: MONO,
  })

  return (
    <div
      style={{
        minHeight: embedded ? undefined : "100vh",
        background: embedded ? "transparent" : "var(--bg, #0a0a0b)",
        color: "var(--text, #f5f5f7)",
        fontFamily: MONO,
        padding: embedded ? "0" : "clamp(16px, 3vw, 40px)",
        display: "flex",
        flexDirection: "column",
        gap: "18px",
      }}
    >
      {/* Top bar: playback controls left, one persona/theme toggle right */}
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
        {embedded ? null : (
          <Link to="/" className="mono" style={{ color: "var(--text)", textDecoration: "none", letterSpacing: "0.18em", display: "inline-flex", alignItems: "center", gap: "10px" }}>
            <img src={isDemon ? "/assets/pixel/demon_16.png" : "/assets/pixel/fairy_16.png"} width={20} height={20} className="pixel" alt="" />
            {isDemon ? "SALES DEMON" : "SALES FAIRY"}
          </Link>
        )}
        <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => setAutoPlay((prev) => !prev)}
            aria-label={autoPlay ? "Pause auto-play" : "Auto-play the demo"}
            style={{ ...pill(autoPlay), padding: "8px 14px" }}
          >
            {autoPlay ? "❚❚" : "▶"}
          </button>
          <button type="button" onClick={restart} aria-label="Restart the demo" style={{ ...pill(false), padding: "8px 14px" }}>
            ↺
          </button>
        </div>
        <button type="button" onClick={toggleTheme} style={pill(false)} aria-label="Switch persona and theme">
          {isDemon ? "Switch to Fairy ✨" : "Switch to Demon 🔥"}
        </button>
      </header>

      {/* Chapter rail: five tappable segments + the current chapter's story */}
      <div style={{ display: "grid", gap: "10px" }}>
        <div style={{ display: "flex", gap: "5px" }} role="tablist" aria-label="Demo chapters">
          {CHAPTERS.map((chapter, index) => (
            <button
              key={chapter.title}
              type="button"
              role="tab"
              aria-selected={index === currentChapter}
              aria-label={`Chapter ${index + 1}: ${chapter.title}`}
              onClick={() => jumpToChapter(index)}
              style={{
                flex: 1,
                border: "none",
                background: "transparent",
                padding: "10px 0",
                cursor: "pointer",
                display: "block",
              }}
            >
              <span
                style={{
                  display: "block",
                  height: "4px",
                  background: index <= currentChapter ? accent : surface.railIdle,
                  transition: "background 0.3s ease",
                }}
              />
            </button>
          ))}
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentChapter}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mono"
            style={{
              border: `1px dashed ${accent}`,
              padding: "10px 14px",
              fontSize: "11px",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              lineHeight: 1.6,
            }}
          >
            <span style={{ color: accentText }}>
              {currentChapter + 1}/{CHAPTERS.length} · {CHAPTERS[currentChapter].title}
            </span>{" "}
            — {CHAPTERS[currentChapter].why}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Mobile pane switch */}
      <div className="demo-pane-switch" style={{ display: "none", gap: "6px" }}>
        <button type="button" onClick={() => setMobilePane("phone")} style={pill(mobilePane === "phone")}>
          Phone
        </button>
        <button type="button" onClick={() => setMobilePane("seen")} style={pill(mobilePane === "seen")}>
          What {isDemon ? "the Demon" : "the Fairy"} sees
        </button>
      </div>

      {/* Panels */}
      <div className={`demo-panels demo-panels--${mobilePane}`} style={{ display: "flex", gap: "24px", alignItems: "flex-start", flexWrap: "wrap" }}>
        {/* Phone */}
        <section
          className="demo-phone"
          style={{
            flex: "0 1 420px",
            minWidth: "min(340px, 100%)",
            border: "1px solid var(--rail)",
            background: surface.panel,
            boxShadow: `0 18px 40px ${isDemon ? "rgba(0,0,0,0.5)" : "rgba(24,32,51,0.12)"}, 0 0 24px ${surface.glow}`,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <header style={{ display: "flex", alignItems: "center", gap: "12px", padding: "14px 16px", borderBottom: "1px solid var(--rail)" }}>
            <img
              src={isDemon ? "/assets/pixel/demon_16.png" : "/assets/pixel/fairy_16.png"}
              width={24}
              height={24}
              className="pixel"
              alt=""
            />
            <div style={{ display: "grid", gap: "2px" }}>
              <strong className="mono" style={{ letterSpacing: "0.14em", fontSize: "13px" }}>{agentName.toUpperCase()}</strong>
              <span className="mono" style={{ fontSize: "10px", color: accentText, letterSpacing: "0.08em" }}>
                ● {agentTagline}
              </span>
            </div>
          </header>

          <div
            ref={threadRef}
            style={{
              padding: "16px",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              height: "min(52vh, 460px)",
              overflowY: "auto",
            }}
          >
            {messages.map((message, index) => {
              const isYou = message.from === "you"
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                  className="mono"
                  style={{
                    alignSelf: isYou ? "flex-end" : "flex-start",
                    maxWidth: "86%",
                    padding: "10px 12px",
                    fontSize: "12.5px",
                    lineHeight: 1.55,
                    border: isYou ? `1px solid ${surface.youBorder}` : `1px solid ${accent}`,
                    background: isYou ? surface.youBg : surface.agentBg,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {personaText(message.text, persona)}
                </motion.div>
              )
            })}
          </div>

          <button
            type="button"
            onClick={atEnd ? restart : advance}
            className="mono"
            style={{
              border: "none",
              borderTop: "1px solid var(--rail)",
              background: "transparent",
              color: accentText,
              padding: "14px",
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              fontSize: "12px",
              cursor: "pointer",
              minHeight: "48px",
            }}
          >
            {atEnd ? "↺ Replay the demo" : "Tap to continue →"}
          </button>
        </section>

        {/* What the agent sees */}
        <section className="demo-seen" style={{ flex: "1 1 380px", minWidth: "min(320px, 100%)", display: "grid", gap: "14px" }}>
          <header className="mono" style={{ letterSpacing: "0.16em", textTransform: "uppercase", fontSize: "11px", color: "var(--muted)" }}>
            What {isDemon ? "your Demon" : "your Fairy"} sees
          </header>

          <div style={{ border: "1px solid var(--rail)", background: surface.panel, padding: "16px", display: "grid", gap: "4px" }}>
            <div className="mono" style={{ fontSize: "12px", color: "var(--muted)", marginBottom: "8px", textTransform: "none" }}>
              Pulled straight from your texts — no forms.
            </div>
            {FIELD_ORDER.map((label) => {
              const value = fields[label]
              return (
                <div
                  key={label}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "16px",
                    borderBottom: `1px solid ${surface.hairline}`,
                    padding: "9px 0",
                  }}
                >
                  <span className="mono" style={{ fontSize: "10px", letterSpacing: "0.14em", color: "var(--muted)", textTransform: "uppercase" }}>
                    {label}
                  </span>
                  <AnimatePresence mode="popLayout">
                    <motion.span
                      key={value ?? "empty"}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mono"
                      style={{ fontSize: "13px", textAlign: "right", color: value ? "var(--text)" : surface.faint }}
                    >
                      {value ?? "—"}
                    </motion.span>
                  </AnimatePresence>
                </div>
              )
            })}
          </div>

          <div style={{ border: "1px solid var(--rail)", background: surface.panel, padding: "16px", display: "grid", gap: "10px" }}>
            <div className="mono" style={{ fontSize: "10px", letterSpacing: "0.16em", color: "var(--muted)", textTransform: "uppercase" }}>
              Guardrails
            </div>
            {checks.length === 0 ? (
              <span className="mono" style={{ fontSize: "12px", color: surface.faint }}>
                Waiting for a money message…
              </span>
            ) : (
              checks.map((check) => {
                const tone =
                  check.state === "clear"
                    ? "var(--success-text)"
                    : check.state === "confirm"
                      ? "var(--warning-text)"
                      : "var(--info-text)"
                const chipLabel = check.state === "clear" ? "CLEAR" : check.state === "confirm" ? "CONFIRM" : "NOTE"
                return (
                  <motion.div
                    key={check.label}
                    initial={{ opacity: 0, x: -6 }}
                    animate={{ opacity: 1, x: 0 }}
                    style={{ display: "flex", alignItems: "center", gap: "10px" }}
                  >
                    <span
                      className="mono"
                      style={{
                        fontSize: "9px",
                        letterSpacing: "0.12em",
                        border: `1px solid ${tone}`,
                        color: tone,
                        padding: "3px 8px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {chipLabel}
                    </span>
                    <span className="mono" style={{ fontSize: "12px", textTransform: "none" }}>{check.label}</span>
                  </motion.div>
                )
              })
            )}
          </div>

          <Link
            to="/onboard/contact"
            className="mono"
            style={{
              justifySelf: "start",
              border: `2px solid ${accent}`,
              background: accent,
              color: "#0a0a0b",
              padding: "14px 22px",
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              fontSize: "12px",
              textDecoration: "none",
              minHeight: "44px",
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            Get your own →
          </Link>
        </section>
      </div>
    </div>
  )
}
